/**
 * AmbiguityResolverDialog — Claude proposes resolutions for bullets
 * with ambiguityFlag set. User accepts each resolution; accepted
 * ones replace the bullet body and clear the flag on
 * meeting.structuredContent.
 */
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/apiClient.ts';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../ui/Modal.tsx';
import type { StructuredContent } from '@ojb/shared';

interface Resolution {
  topicIndex: number;
  bulletIndex: number;
  originalBody: string;
  originalFlag: string;
  proposedBody: string;
  rationale: string;
  confidence: 'high' | 'medium' | 'low';
}

interface UsageMeta {
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  durationMs: number;
}

interface Meeting {
  id: string;
  structuredContent: Record<string, unknown>;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  meeting: Meeting;
  onApply: (next: StructuredContent) => void;
}

export function AmbiguityResolverDialog({
  isOpen,
  onClose,
  meeting,
  onApply,
}: Props) {
  const [accepted, setAccepted] = useState<Set<number>>(new Set());

  const mutation = useMutation({
    mutationFn: () =>
      api.post<{ resolutions: Resolution[]; usage: UsageMeta }>(
        `/meetings/${meeting.id}/resolve-ambiguities`,
      ),
    onSuccess: (data) => {
      setAccepted(
        new Set(
          data.resolutions
            .map((r, i) => (r.confidence === 'high' ? i : -1))
            .filter((i) => i >= 0),
        ),
      );
    },
  });

  useEffect(() => {
    if (isOpen && !mutation.data && !mutation.isPending && !mutation.isError) {
      mutation.mutate();
    }
    if (!isOpen) {
      mutation.reset();
      setAccepted(new Set());
    }
    // (Intentionally narrow deps: only `isOpen` should re-trigger the
    // reset. eslint-plugin-react-hooks isn't currently loaded so the
    // exhaustive-deps disable comment that used to live here would
    // fire a "rule not found" error — removed.)
  }, [isOpen]);

  const resolutions = mutation.data?.resolutions ?? [];
  const acceptedCount = accepted.size;

  const toggle = (i: number) =>
    setAccepted((s) => {
      const next = new Set(s);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  const apply = () => {
    if (acceptedCount === 0) {
      onClose();
      return;
    }
    const current = meeting.structuredContent as unknown as StructuredContent;
    const topics = (current.topics ?? []).map((t) => ({
      ...t,
      bullets: t.bullets.map((b) => ({ ...b })),
    }));
    for (const i of accepted) {
      const r = resolutions[i];
      if (!r) continue;
      const topic = topics[r.topicIndex];
      if (!topic) continue;
      const bullet = topic.bullets[r.bulletIndex];
      if (!bullet) continue;
      bullet.body = r.proposedBody;
      bullet.ambiguityFlag = undefined;
    }
    onApply({ ...current, topics });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      ariaLabel="Resolve ambiguity flags"
    >
      <ModalHeader
        title="Resolve ambiguity flags"
        subtitle="Claude proposes concrete resolutions for every flagged bullet, using the surrounding topic + project memory. Accept the ones that look right."
        onClose={onClose}
      />
      <ModalBody>
        {mutation.isPending && (
          <div className="py-10 text-center text-sm italic text-charcoal/60">
            Claude is reading the flagged bullets…
          </div>
        )}
        {mutation.isError && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {mutation.error instanceof ApiError
              ? mutation.error.message
              : 'Resolve failed'}
          </div>
        )}
        {mutation.data && resolutions.length === 0 && (
          <div className="rounded-2xl border border-dashed border-charcoal/20 p-6 text-center text-sm italic text-charcoal/60">
            No resolvable ambiguities found. Either there are no
            flagged bullets, or Claude couldn&rsquo;t confidently
            resolve them from the available context.
          </div>
        )}
        {resolutions.length > 0 && (
          <ul className="space-y-3">
            {resolutions.map((r, i) => {
              const isAccepted = accepted.has(i);
              return (
                <li
                  key={`${r.topicIndex}-${r.bulletIndex}-${i}`}
                  className={
                    'rounded-2xl border p-4 transition-colors ' +
                    (isAccepted
                      ? 'border-pine/40 bg-pine/5'
                      : 'border-charcoal/15 bg-white')
                  }
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={isAccepted}
                      onChange={() => toggle(i)}
                      className="mt-1 h-4 w-4 accent-pine"
                    />
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <ConfidencePill level={r.confidence} />
                        <span className="text-[10px] text-charcoal/50">
                          Topic {r.topicIndex + 1} · Bullet {r.bulletIndex + 1}
                        </span>
                      </div>
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-charcoal/60">
                          Original
                        </div>
                        <div className="mt-1 text-sm text-charcoal/80">
                          {r.originalBody}
                        </div>
                        <div className="mt-1 text-[11px] italic text-amber-700">
                          ⚠ {r.originalFlag}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-pine">
                          Proposed
                        </div>
                        <div className="mt-1 rounded-lg border border-pine/20 bg-pine/5 px-3 py-2 text-sm text-charcoal">
                          {r.proposedBody}
                        </div>
                        <div className="mt-1 text-[11px] text-charcoal/55">
                          {r.rationale}
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </ModalBody>
      <ModalFooter className="flex items-center justify-between gap-2">
        <div className="text-[11px] text-charcoal/60">
          {acceptedCount > 0
            ? `${acceptedCount} selected`
            : resolutions.length > 0
              ? 'None selected'
              : ''}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-charcoal/25 px-4 py-2 text-sm text-charcoal transition-colors hover:border-pine hover:text-pine"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={acceptedCount === 0}
            className="rounded-full bg-pine px-4 py-2 text-sm font-semibold text-cloud transition-colors hover:bg-pine/90 disabled:opacity-50"
          >
            Apply {acceptedCount > 0 ? `${acceptedCount}` : 'selected'}
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
}

function ConfidencePill({ level }: { level: 'high' | 'medium' | 'low' }) {
  const map = {
    high: 'bg-pine/10 text-pine',
    medium: 'bg-amber-100 text-amber-800',
    low: 'bg-charcoal/10 text-charcoal/70',
  };
  return (
    <span
      className={
        'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ' +
        map[level]
      }
    >
      {level} confidence
    </span>
  );
}
